/**
 * S21 — mounts ViewMatrixView for an active view carrying a `matrix` clause.
 * Same shape as ViewTableContainer: the projection comes from the SAME
 * machinery the board uses (`useViewSurface` → `computeViewSurfaceResult`) —
 * governance, live knobs, view-INSTANCE parent resolution, the plane pick, and
 * the crash guard (a throwing projection renders nothing + a HUD diagnostic,
 * never an unmounted tree). An empty cell on key axes scaffolds `row > col`
 * through the WYSIWYG mutation service (`addProcessWithStep`, the §3.3
 * zero-prompt anonymous process → S13a `scaffoldEdgeEdit`/`scaffoldProcessEdit`
 * under the hood): `matrixScaffoldArgs` (matrixScaffold.ts) picks the FROM
 * module's file, the common-ancestor anchor, and minimal operand suffixes. The
 * host opts in with `editable`; without it — or without a workspace store —
 * the matrix renders display-only.
 */
import { useCallback, useMemo, type ReactNode } from "react";
import { computeMatrixData } from "@archlang/engine";
import { useWorkspaceModel } from "../workbench/WorkspaceModelContext.js";
import { useModelMutationsOptional } from "../workbench/mutations.js";
import { useViewSurface } from "./useViewSurface.js";
import { matrixScaffoldArgs } from "./matrixScaffold.js";
import { ViewMatrixView } from "./ViewMatrixView.js";
import { useRegisterViewExport } from "./ViewExportContext.js";
import type { ViewExportAction } from "./export/ViewExportMenu.js";
import { matrixToCsv, downloadText } from "./export/viewExport.js";
import { useTranslation } from "../i18n";

export function ViewMatrixContainer({ editable = false }: { editable?: boolean }): ReactNode {
  const { t } = useTranslation();
  const { graph } = useWorkspaceModel();
  const { view, enginePlan, ctx } = useViewSurface();
  const mutations = useModelMutationsOptional();
  const data = useMemo(() => {
    if (!view || !ctx) return null;
    try {
      return computeMatrixData(view, enginePlan, ctx);
    } catch (e) {
      // Same crash posture as the board's plan computation — degrade, never
      // unmount the tree.
      console.debug("[view-matrix] matrix data evaluation failed:", e);
      return null;
    }
  }, [view, enginePlan, ctx]);

  const onScaffold = useCallback(
    (rowKey: string, colKey: string): void => {
      if (!mutations || !graph) return;
      const args = matrixScaffoldArgs([...graph.pathOfModule.values()], rowKey, colKey);
      if (!args) return; // diagonal — never scaffolds
      mutations.addProcessWithStep(args);
    },
    [mutations, graph],
  );

  const exportActions = useMemo<ViewExportAction[]>(
    () =>
      data
        ? [{ label: t("indexExportCsv"), run: () => downloadText(matrixToCsv(data), "matrix.csv", "text/csv") }]
        : [],
    [data, t],
  );
  useRegisterViewExport(exportActions);

  if (!data) return null;
  return (
    <ViewMatrixView
      data={data}
      onScaffold={editable && mutations ? onScaffold : undefined}
      focus={enginePlan?.focus}
    />
  );
}
